<template>
  <v-container class="pa-2 motorista-screen" fluid>
    <v-toolbar dense flat color="primary" dark class="mb-2 rounded">
      <v-toolbar-title class="text-subtitle-1 font-weight-medium">
        {{ $t('guiasMotorista.jornadaScreenTitle') }}
      </v-toolbar-title>
      <v-spacer />
      <v-btn icon :title="$t('guide.reload')" @click="loadJornadaAtiva" :loading="jornadaLoading">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
      <v-btn icon :title="$t('button.logout')" @click="logout">
        <v-icon>mdi-logout</v-icon>
      </v-btn>
    </v-toolbar>

    <v-alert v-if="!idFuncionario" type="warning" dense outlined class="mb-3">
      {{ $t('guiasMotorista.noDriverLinked') }}
    </v-alert>

    <v-card v-if="idFuncionario" outlined class="mb-3">
      <v-card-text class="py-4">
        <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px;">
          <v-icon large :color="jornadaAtiva ? 'success' : 'grey'">mdi-clock-outline</v-icon>
          <div style="max-width: 90%;">
            <div class="text-h6 font-weight-medium">{{ $t('guiasMotorista.jornadaTitle') }}</div>
            <div v-if="jornadaLoading" class="text-body-2 text--secondary mt-1">...</div>
            <div v-else-if="jornadaAtiva" class="text-body-2 text--secondary mt-1">
              {{ $t('guiasMotorista.jornadaActiveSince', { hora: jornadaAtiva.dt_inicio_fmt || '—' }) }}
              <span v-if="jornadaCercaInicioLabel">
                · {{ $t('guiasMotorista.jornadaInsideCerca', { cerca: jornadaCercaInicioLabel }) }}
              </span>
            </div>
            <div v-else class="text-body-2 text--secondary mt-1">
              {{ $t('guiasMotorista.jornadaInactive') }}
            </div>
          </div>
        </div>

        <p v-if="!jornadaAtiva && !jornadaLoading" class="text-body-2 text--secondary mb-4">
          {{ $t('guiasMotorista.jornadaStartHint') }}
        </p>

        <v-btn
          v-if="!jornadaAtiva"
          block
          large
          color="success"
          class="mb-3"
          :disabled="jornadaLoading || jornadaSalvando"
          @click="openJornadaConfirm('inicio')"
        >
          <v-icon left>mdi-play-circle-outline</v-icon>
          {{ $t('guiasMotorista.jornadaStart') }}
        </v-btn>

        <template v-else>
          <v-btn
            block
            large
            color="error"
            outlined
            :disabled="jornadaLoading || jornadaSalvando"
            @click="openJornadaConfirm('fim')"
          >
            <v-icon left>mdi-stop-circle-outline</v-icon>
            {{ $t('guiasMotorista.jornadaEnd') }}
          </v-btn>
        </template>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialogJornadaConfirm" persistent max-width="420">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-medium">
          {{ jornadaConfirmTitle }}
        </v-card-title>
        <v-card-text class="text-body-2">
          {{ jornadaConfirmMessage }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="jornadaSalvando" @click="cancelJornadaConfirm">
            {{ $t('button.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="jornadaSalvando"
            :disabled="jornadaSalvando"
            @click="confirmJornada"
          >
            {{ $t('button.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios';
import { ApiBaseUrl } from '@/constants';
import EventBus from '@/utils/eventBus';

export default {
  name: 'JornadaMotorista',
  data() {
    return {
      token: null,
      idFuncionario: null,
      jornadaAtiva: null,
      jornadaLoading: false,
      jornadaSalvando: false,
      dialogJornadaConfirm: false,
      jornadaConfirmAction: null,
    };
  },
  computed: {
    inicioJornadaObrigatorio() {
      const getter = this.$store && this.$store.getters
        ? this.$store.getters['config/motoristaInicioJornadaObrigatorio']
        : true;
      return getter !== false;
    },
    jornadaCercaInicioLabel() {
      const j = this.jornadaAtiva;
      if (!j) return '';
      return j.nome_cerca_inicio || (j.id_cerca_inicio != null ? String(j.id_cerca_inicio) : '');
    },
    jornadaConfirmTitle() {
      if (this.jornadaConfirmAction === 'fim') {
        return this.$t('guiasMotorista.jornadaConfirmEndTitle');
      }
      return this.$t('guiasMotorista.jornadaConfirmStartTitle');
    },
    jornadaConfirmMessage() {
      if (this.jornadaConfirmAction === 'fim') {
        return this.$t('guiasMotorista.jornadaConfirmEndMessage');
      }
      return this.$t('guiasMotorista.jornadaConfirmStartMessage');
    },
  },
  created() {
    const raw = sessionStorage.getItem('user');
    if (raw) {
      try {
        const u = JSON.parse(raw);
        this.idFuncionario = u.id_funcionario != null ? u.id_funcionario : null;
      } catch (e) {
        this.idFuncionario = null;
      }
    }
    this.token = sessionStorage.getItem('api');
    if (!this.inicioJornadaObrigatorio) {
      this.$router.replace({ path: '/guiasmotorista/guias' });
      return;
    }
    this.loadJornadaAtiva();
  },
  methods: {
    logout() {
      sessionStorage.removeItem('api');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('permissoes');
      this.$router.push({ path: '/' });
    },
    goToGuias() {
      this.$router.push({ path: '/guiasmotorista/guias' });
    },
    loadJornadaAtiva() {
      if (!this.token || !this.idFuncionario) return;
      this.jornadaLoading = true;
      axios
        .get(`${ApiBaseUrl}/api/v1.0/getjornadamotoristaativa`, {
          headers: { 'x-access-token': this.token },
        })
        .then((res) => {
          const payload = res && res.data ? res.data : {};
          this.jornadaAtiva = payload.jornada || null;
        })
        .catch(() => {
          this.jornadaAtiva = null;
        })
        .finally(() => {
          this.jornadaLoading = false;
        });
    },
    getCurrentPositionForJornada() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('geolocation_unavailable'));
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (!pos || !pos.coords) {
              reject(new Error('position_unavailable'));
              return;
            }
            const lat = Number(pos.coords.latitude);
            const lng = Number(pos.coords.longitude);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
              reject(new Error('invalid_coords'));
              return;
            }
            resolve({ lat, lng });
          },
          (err) => reject(err || new Error('geolocation_error')),
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
        );
      });
    },
    openJornadaConfirm(action) {
      if (!this.token || !this.idFuncionario) return;
      this.jornadaConfirmAction = action;
      this.dialogJornadaConfirm = true;
    },
    cancelJornadaConfirm() {
      if (this.jornadaSalvando) return;
      this.dialogJornadaConfirm = false;
      this.jornadaConfirmAction = null;
    },
    async confirmJornada() {
      const action = this.jornadaConfirmAction;
      if (!action) return;
      const ok = await this.jornadaTap(action);
      if (ok) {
        this.dialogJornadaConfirm = false;
        this.jornadaConfirmAction = null;
        if (action === 'inicio') {
          this.goToGuias();
        }
      }
    },
    async jornadaTap(action) {
      if (!this.token || !this.idFuncionario) return false;
      this.jornadaSalvando = true;
      try {
        const coords = await this.getCurrentPositionForJornada();
        const res = await axios.post(
          `${ApiBaseUrl}/api/v1.0/postjornadamotorista`,
          { action, lat: coords.lat, lng: coords.lng },
          { headers: { 'x-access-token': this.token } },
        );
        const payload = res && res.data ? res.data : {};
        this.jornadaAtiva = payload.jornada || null;
        const okMsg =
          action === 'inicio'
            ? this.$t('guiasMotorista.jornadaStartSuccess')
            : this.$t('guiasMotorista.jornadaEndSuccess');
        EventBus.$emit('show-alert', { mensagem: okMsg, tipo: 'success' });
        if (action === 'fim') {
          const checkoutCount = payload.guias_checkout != null ? Number(payload.guias_checkout) : 0;
          if (checkoutCount > 0) {
            EventBus.$emit('show-alert', {
              mensagem: this.$t('guiasMotorista.jornadaEndCheckoutGuides', { n: checkoutCount }),
              tipo: 'info',
            });
          }
        }
        EventBus.$emit('motorista-jornada-updated');
        return true;
      } catch (e) {
        if (!e || !e.response) {
          EventBus.$emit('show-alert', {
            mensagem: this.$t('guiasMotorista.jornadaLocationError'),
            tipo: 'warning',
          });
          return false;
        }
        const status = e.response.status;
        const data = e.response.data;
        let msg = data && data.mensagem ? data.mensagem : this.$t('guiasMotorista.jornadaError');
        if (status === 409) {
          msg = data && data.mensagem ? data.mensagem : this.$t('guiasMotorista.jornadaAlreadyActive');
          if (data && data.jornada) this.jornadaAtiva = data.jornada;
          EventBus.$emit('motorista-jornada-updated');
        } else if (status === 400 && action === 'fim') {
          msg = data && data.mensagem ? data.mensagem : this.$t('guiasMotorista.jornadaNoActive');
        }
        EventBus.$emit('show-alert', { mensagem: msg, tipo: status === 409 ? 'warning' : 'error' });
        return false;
      } finally {
        this.jornadaSalvando = false;
      }
    },
  },
};
</script>
